import isNil from 'lodash.isnil';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import { format } from 'date-fns';
import last from 'lodash.last';
import React from 'react';
import {
    INDEX as PM_INDEX,
    PM_ROW_SPAN,
    PM_SCORES_NUMBER,
    TITLE as PM_TITLES,
    PROGRESS_HEADER_VIEW,
} from '../../constants/shared/PMConstants';
import { CELL_TYPE, CLASS_NAME, INDEX as CELL_INDEX } from '../../constants/shared/CellConstants';
import {
    MEASURE,
    MEASURES_WITH_PERCENTAGE,
    hasLastInterventionSkill,
} from '../../constants/reading/MeasuresConstants';
import { TextCell } from '../../components/cell/shared/Cells';
import {
    getBenchmarkScoreCell,
    getCompositeCell,
    getProgressGridHeader,
    getStudentNameCommonCell,
    getTwoPartHeaderCell,
} from '../../features/progress/shared/progressHelper';
import { getPercentageSign, isMeasureEnabledForSpecificMapper } from '../shared/tableUtils';
import { DATE_FORMAT, PERIOD } from '../../constants/shared/CommonConstants';
import { SS_URL } from '../../constants/reading/ApiConstants';
import {
    getGoalByMeasureYearGradePeriod,
    getPmStatusesByPeriod,
    getStudentResultByMeasureYearGradePeriod,
} from '../shared/filterUtils';
import { getGoalByMeasureGradePeriod, getSpecificReadingGoal, isBurstMeasure } from './filterUtils';
import { getSortedResultsByDate } from '../shared/sortUtils';
import {
    getCursorPointerClassName,
    getEndPmPeriod,
    getStartPmPeriod,
    handleScoreClick,
    getAimline,
    getTeacher,
    getPmHeaderRow,
    isStatusLastWebEntry,
    getRowsData,
    buildPmProgressHeader,
    getProgressDividedColSpan,
    getStudentsRowObjects,
    getRowsForGrade,
} from '../shared/pmUtils';
import CommonCellBuilder from '../../components/cell/reading/CommonCellBuilder';
import SkillCell from '../../components/cell/reading/Cells';
import InterventionGroupLink from '../../features/intervention/reading/InterventionGroupLink';

const { FIRST_INTERVENTION_HEADER, LAST_INTERVENTION_HEADER } = PM_INDEX;
const { INTERVENTION_TITLE } = PM_TITLES;
const { WIDE_COLUMN, LEFT_ALIGN, MIDDLE_COLUMN } = CLASS_NAME;
const { FIRST_ARRAY_INDEX, FIRST_GRID_ROW } = CELL_INDEX;
const { INPUT, BENCHMARK_GATING_MEASURE } = CELL_TYPE;
const { EOY, AFTER_EOY } = PERIOD;

export const isAimlineAvailable = (measure, period, isOffGrade) => {
    if (period === EOY || isOffGrade || measure === MEASURE.TRC) return false;
    return isNil(measure) || !isBurstMeasure(measure);
};

export const isNextGoalAvailable = (pmPeriod, isOffGrade, measure) =>
    pmPeriod !== AFTER_EOY && !isOffGrade && !isBurstMeasure(measure);

const buildPmScoreCells = ({
    pmStatuses,
    measure,
    grade,
    period,
    openProbeDetails,
    openMazeWebEntry,
    externalId,
    location,
    isOffGrade,
    getGradeToMeasureMapper,
}) => {
    const scoreCells = {};
    for (let index = FIRST_ARRAY_INDEX; index < PM_SCORES_NUMBER; index += 1) {
        const pmStatus = get(pmStatuses, index, {});
        const { resultId, withPencil } = pmStatus;
        let cellType;
        if (withPencil) {
            cellType = INPUT;
        } else {
            cellType = isEmpty(pmStatus) ? BENCHMARK_GATING_MEASURE : CELL_TYPE.BENCHMARK;
        }
        scoreCells[`pmScore${index + 1}`] = (
            <CommonCellBuilder
                cellType={cellType}
                statusForMeasureYearPeriod={pmStatus}
                measure={measure}
                period={period}
                grade={grade}
                externalId={externalId}
                location={location}
                openPanel={handleScoreClick(
                    resultId,
                    withPencil,
                    openProbeDetails,
                    openMazeWebEntry,
                )}
                optionalCellData={{
                    classNames: `${getCursorPointerClassName(resultId)} ${
                        index === PM_SCORES_NUMBER - 1 ? '' : 'right-short-border'
                    }`,
                }}
                isCellDisabled={
                    !isMeasureEnabledForSpecificMapper(
                        measure,
                        grade,
                        period,
                        getGradeToMeasureMapper,
                    ) || isOffGrade
                }
            />
        );
    }
    return scoreCells;
};

const buildStudentRow = ({
    pmPeriod,
    studentInfo,
    composite,
    location,
    openProbeDetails,
    openMazeWebEntry,
    externalId,
    nextToyStartDate,
    withBurstMark = false,
    dataByMeasure = {},
    getGradeToMeasureMapper,
}) => {
    const {
        measure,
        grade,
        isOffGrade,
        status,
        pmStatuses = [],
        nextGoal,
        customGoal,
    } = dataByMeasure;
    const lastDate = !isEmpty(pmStatuses) ? last(pmStatuses).date : null;
    const period = getStartPmPeriod(pmPeriod);
    const resultId = get(status, 'resultId');
    const commonBenchmarkParams = {
        externalId,
        measure,
        grade,
        location,
        isOffGrade,
    };
    const benchmarkScoreParams = {
        ...commonBenchmarkParams,
        statusForMeasureYearPeriod: status,
        period,
        optionalCellData: {
            classNames: `${getCursorPointerClassName(resultId)}`,
        },
        openPanel: isNil(resultId) ? () => {} : openProbeDetails,
        isCellDisabled:
            isOffGrade ||
            !isMeasureEnabledForSpecificMapper(measure, grade, period, getGradeToMeasureMapper),
    };

    const isNextGoal = isNextGoalAvailable(pmPeriod, isOffGrade, measure);

    let nextPeriodGoalCell;
    if (isNextGoal) {
        const cellContent = isNil(nextGoal)
            ? null
            : `${nextGoal}${getPercentageSign(MEASURES_WITH_PERCENTAGE, measure)}`;

        nextPeriodGoalCell = <TextCell content={cellContent} />;
    } else {
        nextPeriodGoalCell = <TextCell classNames="empty-cell" />;
    }

    let goalSetCell;
    if (measure !== MEASURE.TRC && isNextGoal) {
        const cellContent = isNil(customGoal)
            ? null
            : `${customGoal}${getPercentageSign(MEASURES_WITH_PERCENTAGE, measure)}`;

        goalSetCell = <TextCell content={cellContent} />;
    } else {
        goalSetCell = <TextCell classNames="empty-cell" />;
    }

    return {
        ...getStudentNameCommonCell(studentInfo, location, SS_URL.PROGRESS),
        ...getCompositeCell('composite', composite),
        skill: (
            <SkillCell
                measure={measure}
                withBurstMark={withBurstMark}
                grade={isOffGrade ? grade : null}
            />
        ),

        ...getBenchmarkScoreCell(
            'period',
            benchmarkScoreParams,
            isEmpty(dataByMeasure),
            CommonCellBuilder,
        ),
        ...buildPmScoreCells({
            pmStatuses,
            measure,
            grade,
            period,
            openProbeDetails,
            openMazeWebEntry,
            externalId,
            location,
            isOffGrade,
            getGradeToMeasureMapper,
        }),
        nextPeriod: nextPeriodGoalCell,
        goalSet: goalSetCell,
        aimline: getAimline(
            measure,
            status,
            pmStatuses,
            { score: customGoal, date: nextToyStartDate },
            isAimlineAvailable(measure, period, isOffGrade),
        ),
        lastAssessed: (
            <TextCell
                content={lastDate ? format(new Date(lastDate), DATE_FORMAT.FULL_DATE) : null}
                classNames={MIDDLE_COLUMN}
            />
        ),
    };
};

const isStatusEditable = (status, pmStatuses) =>
    isStatusLastWebEntry(status, pmStatuses, MEASURE.MAZE);

const getPmWithInterventionHeaderRow = (pmPeriod, grade) => ({
    ...getPmHeaderRow(pmPeriod, grade),
    ...getTwoPartHeaderCell('groupName', WIDE_COLUMN, '', '', 'Group name', true),
    ...getTwoPartHeaderCell('burstsNumber', MIDDLE_COLUMN, '', '# of Lesson', 'Sets', true, true),
    ...getTwoPartHeaderCell('burstTeacher', WIDE_COLUMN, '', '', 'Intervention Teacher', true),
    ...getTwoPartHeaderCell('classroomTeacher', WIDE_COLUMN, '', '', 'Classroom Teacher', true),
});

const getStudentPmRows = (
    pmPeriod,
    nextToyStartDate,
    studentData,
    location,
    openProbeDetails,
    openMazeWebEntry,
    getGradeToMeasureMapper,
    interventionSkills = [],
) => {
    const { studentInfo, composite, data } = studentData;
    const { id: externalId } = studentInfo;
    if (isEmpty(data)) {
        return Array.of(
            buildStudentRow({
                pmPeriod,
                studentInfo,
                composite,
                location,
                getGradeToMeasureMapper,
            }),
        );
    }
    return data.map((dataByMeasure, index) => {
        const withBurstMark = hasLastInterventionSkill(interventionSkills, dataByMeasure.measure);
        const isFirstIndex = index === FIRST_ARRAY_INDEX;
        return buildStudentRow({
            pmPeriod,
            studentInfo: isFirstIndex ? studentInfo : {},
            composite: isFirstIndex ? composite : {},
            location,
            openProbeDetails,
            openMazeWebEntry,
            externalId,
            nextToyStartDate,
            withBurstMark,
            dataByMeasure,
            getGradeToMeasureMapper,
        });
    });
};

export const buildInterventionRow = (index, location, intervention = {}) => {
    const { numberOfBursts, burstTeacher, classroomTeacher, groupName } = intervention;
    const isDataCell = index === FIRST_ARRAY_INDEX;
    const classNames = isDataCell ? '' : PM_ROW_SPAN;
    const wideColumnClassNames = `${WIDE_COLUMN} ${classNames}`;
    const showInterventionValue = !!groupName || numberOfBursts === 0;
    const getValue = (interventionValue, defaultValue) =>
        showInterventionValue ? interventionValue : defaultValue;
    const showEmptyCell = getValue('', 'empty-cell-middle');

    // burstsNumber is -1 for sorting. content - null, to leave the cell empty
    const burstsNumber = getValue(numberOfBursts, -1);
    const content = getValue(numberOfBursts, null);

    return {
        groupName: (
            <TextCell
                classNames={`${wideColumnClassNames} ${LEFT_ALIGN} text-left`}
                isDataCell={isDataCell}
                content={
                    <InterventionGroupLink
                        groupData={intervention}
                        location={location}
                        className="black-color"
                    />
                }
            >
                {groupName}
            </TextCell>
        ),
        burstsNumber: (
            <TextCell
                classNames={`${MIDDLE_COLUMN} ${classNames} ${showEmptyCell}`}
                content={content}
                isDataCell={isDataCell}
            >
                {burstsNumber}
            </TextCell>
        ),
        burstTeacher: getTeacher(burstTeacher, wideColumnClassNames, isDataCell),
        classroomTeacher: getTeacher(classroomTeacher, wideColumnClassNames, isDataCell),
    };
};

const getStudentPmWithInterventionRows = (
    pmPeriod,
    nextToyStartDate,
    studentData,
    location,
    openProbeDetails,
    openMazeWebEntry,
    intervention,
    gradeToMeasureMapper,
) => {
    const interventionSkills = get(intervention, 'lastInterventionSkills', []);
    return getStudentPmRows(
        pmPeriod,
        nextToyStartDate,
        studentData,
        location,
        openProbeDetails,
        openMazeWebEntry,
        gradeToMeasureMapper,
        interventionSkills,
    )?.map((studentRow, index) => ({
        ...studentRow,
        ...buildInterventionRow(index, location, index === FIRST_ARRAY_INDEX ? intervention : {}),
    }));
};

export const getPmGridData = (
    statuses,
    currentGrade,
    pmPeriod,
    windowDates,
    interps,
    goals,
    toyPeriods,
) => {
    const completedPmStatusesByPmPeriod = getPmStatusesByPeriod(
        statuses,
        pmPeriod,
        windowDates,
        toyPeriods,
    ).filter(status => status.isComplete);
    const rowsData = getRowsData(completedPmStatusesByPmPeriod, isStatusEditable);
    const sortedRowsData = getSortedResultsByDate(rowsData, 'lastDate');
    const pmGridData = [];
    sortedRowsData.forEach(({ measure, grade, pmStatuses }) => {
        const status = getStudentResultByMeasureYearGradePeriod(
            statuses,
            measure,
            null,
            grade,
            getStartPmPeriod(pmPeriod),
        );
        const nextGoal = getGoalByMeasureGradePeriod(
            interps,
            measure,
            grade,
            getEndPmPeriod(pmPeriod),
            getSpecificReadingGoal(measure),
        );

        const customGoal = getGoalByMeasureYearGradePeriod(
            goals,
            measure,
            null,
            grade,
            getEndPmPeriod(pmPeriod),
        );

        pmGridData.push({
            measure,
            grade,
            isOffGrade: currentGrade !== grade,
            pmStatuses,
            nextGoal,
            customGoal,
            status,
        });
    });
    return pmGridData;
};

export const getInterventionForStudentAndPmPeriod = (
    interventionsByPmPeriods,
    studentId,
    pmPeriod,
) => {
    const interventions = get(interventionsByPmPeriods, `${pmPeriod}`, []);
    return interventions.find(intervention => intervention.externalId === studentId);
};

const buildRowsForGrade = ({
    resultByGrade,
    withIntervention,
    pmPeriod,
    windowDates,
    location,
    openMazeWebEntry,
    openProbeDetails,
    interventionsByPmPeriods,
    getGradeToMeasureMapper,
}) => {
    const { studentsData, grade } = resultByGrade;
    const period = getEndPmPeriod(pmPeriod);
    const nextToyStartDate = get(windowDates, `${period}.startDate`);
    const studentsRowObjects = [];
    studentsData.forEach(studentData => {
        const { studentInfo } = studentData;
        const studentRows = withIntervention
            ? getStudentPmWithInterventionRows(
                  pmPeriod,
                  nextToyStartDate,
                  studentData,
                  location,
                  openProbeDetails,
                  openMazeWebEntry,
                  getInterventionForStudentAndPmPeriod(
                      interventionsByPmPeriods,
                      get(studentInfo, 'id'),
                      pmPeriod,
                  ),
                  getGradeToMeasureMapper,
              )
            : getStudentPmRows(
                  pmPeriod,
                  nextToyStartDate,
                  studentData,
                  location,
                  openProbeDetails,
                  openMazeWebEntry,
                  getGradeToMeasureMapper,
                  [],
              );
        getStudentsRowObjects(studentsRowObjects, studentRows);
    });
    const rowObject = withIntervention
        ? getPmWithInterventionHeaderRow(pmPeriod, grade)
        : getPmHeaderRow(pmPeriod, grade);
    return getRowsForGrade(studentsRowObjects, rowObject);
};

const buildPmHeaderWithIntervention = withIntervention => {
    return withIntervention
        ? [
              buildPmProgressHeader(),
              getProgressGridHeader(
                  FIRST_GRID_ROW,
                  FIRST_INTERVENTION_HEADER,
                  LAST_INTERVENTION_HEADER,
                  INTERVENTION_TITLE,
                  'blue',
              ),
          ]
        : [buildPmProgressHeader()];
};

export const getRowsToRender = ({
    resultsByGrades,
    pmPeriod,
    windowDates,
    location,
    openMazeWebEntry,
    openProbeDetails,
    withIntervention,
    interventionsByPmPeriods,
    getGradeToMeasureMapper,
}) => {
    const rowsToRenderObject = {};
    const studentRowsByGrades = [];
    resultsByGrades.forEach(resultByGrade => {
        studentRowsByGrades.push(
            ...buildRowsForGrade({
                resultByGrade,
                withIntervention,
                pmPeriod,
                windowDates,
                location,
                openMazeWebEntry,
                openProbeDetails,
                interventionsByPmPeriods,
                getGradeToMeasureMapper,
            }),
        );
    });
    rowsToRenderObject.rowSpans = [PROGRESS_HEADER_VIEW, ...studentRowsByGrades];
    rowsToRenderObject.colSpans = [
        ...buildPmHeaderWithIntervention(withIntervention),
        ...getProgressDividedColSpan(resultsByGrades),
    ];
    return rowsToRenderObject;
};
